<template>
  <div class="flex flex-col h-full relative mx-auto w-full support-canvas">
    <!-- Overlay de prévisualisation du scale PDF -->
    <div v-if="showScalePreview" class="scale-preview-overlay">
      <div class="scale-preview-content">
        <div class="scale-preview-header">
          Prévisualisation du rendu PDF (SCALE appliqué)
        </div>
        <div class="scale-preview-canvas-wrapper">
          <div class="banner-canvas-preview" :style="scalePreviewStyle">
            <div ref="bannerCanvasDomRef" />
          </div>
        </div>
        <div class="scale-preview-actions">
          <button class="btn-continue" @click="continuePdfGeneration">
            Continuer
          </button>
          <button class="btn-cancel" @click="cancelScalePreview">
            Annuler
          </button>
        </div>
      </div>
    </div>
    <!-- Overlay de chargement PDF (existant) -->
    <div v-if="isSaving" class="pdf-loading-overlay">
      <div class="pdf-loading-content loader-standard">
        <div class="spinner" />
        <div class="loading-text">
          Enregistrement en cours…
        </div>
      </div>
    </div>
    <!-- Boutons de contrôle -->
    <div class="flex justify-between items-center m-2">
      <div class="flex gap-2">
        <button
          class="preview-toggle"
          :class="{ active: !hasImage }"
          @click="handleSansImage"
        >
          Sans Image
        </button>
        <button
          class="preview-toggle"
          :class="{ active: hasImage }"
          @click="handleAvecImage"
        >
          Avec Image
        </button>
        <!-- Contrôles de style du titre -->
        <div class="flex gap-2 items-center">
          <select v-model="selectedFont" class="format-select">
            <option value="Arial">
              Arial
            </option>
            <option value="Helvetica">
              Helvetica
            </option>
            <option value="Times New Roman">
              Times New Roman
            </option>
            <option value="Georgia">
              Georgia
            </option>
          </select>
          <button
            class="format-button"
            :class="{ active: isBold }"
            title="Gras"
            @click="toggleBold"
          >
            <span class="font-bold">B</span>
          </button>
          <button
            class="format-button"
            :class="{ active: isItalic }"
            title="Italique"
            @click="toggleItalic"
          >
            <span class="italic">I</span>
          </button>
          <div class="flex items-center gap-1">
            <button
              class="format-button"
              title="Diminuer la taille"
              :disabled="!canDecreaseFontSize"
              :class="{ 'opacity-50 cursor-not-allowed': !canDecreaseFontSize }"
              @click="decreaseFontSize"
            >
              <span>A-</span>
            </button>
            <button
              class="format-button"
              title="Augmenter la taille"
              :disabled="!canIncreaseFontSize"
              :class="{ 'opacity-50 cursor-not-allowed': !canIncreaseFontSize }"
              @click="increaseFontSize"
            >
              <span>A+</span>
            </button>
          </div>
        </div>
      </div>
      <button
        class="preview-toggle"
        :class="{ active: isPreviewMode }"
        @click="togglePreview"
      >
        <span class="icon">👁️</span>
        {{ !isPreviewMode ? "Aperçu" : "Mode Édition" }}
      </button>
    </div>
    <!-- Zone de contenu -->
    <div
      class="banner-canvas bg-white flex-grow flex relative max-h-[240px] mx-auto w-full shadow-lg border-t"
      :class="{ 'preview-mode': isPreviewMode }"
    >
      <div class="flex w-full h-full">
        <div
          :class="
            hasImage
              ? 'flex-grow textZone flex flex-col'
              : 'w-full flex flex-col'
          "
          :style="hasImage ? 'width: 70%' : 'width: 100%'"
        >
          <BanderoleHeader class="relative z-10" :has-image="hasImage" />
          <div
            class="flex-grow relative title-container flex items-center"
            :class="brand === 'valtra' ? 'valtra-title-container' : ''"
            style="overflow: visible; word-break: break-word"
          >
            <template v-if="!isPreviewMode">
              <textarea
                ref="titleRef"
                v-model="titleHtml"
                :class="['title-textarea', titleWidthClass]"
                rows="2"
                placeholder="Titre de l'offre"
                :style="{
                  fontSize: titleFontSize + 'px',
                  fontFamily: titleFontFamily,
                  fontWeight: titleFontWeight,
                  fontStyle: titleFontStyle,
                  lineHeight: 1.3,
                  overflow: 'visible',
                  wordBreak: 'break-word',
                }"
                @blur="handleTitleBlur"
              />
            </template>
            <template v-else>
              <h1
                class="text-6xl font-light outline-none"
                :class="[titleWidthClass]"
                :style="{
                  ...titleStyle,
                  overflow: 'visible',
                  wordBreak: 'break-word',
                }"
              >
                {{ titleHtml }}
              </h1>
            </template>
          </div>
        </div>
        <div
          v-if="hasImage"
          class="w-[30%] flex-shrink-0 flex items-center justify-center h-auto"
        >
          <ProductBlockImage
            :product="props.selectedProduct"
            :is-preview="isPreviewMode"
            variant="product-image"
            class="max-h-full flex items-center block-product-image"
            @dragover.prevent
            @dragleave.prevent
            @drop.prevent="onProductDrop"
            @remove="removeProduct"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "BanderoleCanvas",
};
</script>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "@vue/runtime-core";
import domtoimage from "dom-to-image-more";
import jsPDF from "jspdf";
import { useToast } from "vue-toastification";
import type { Product } from "../../../types/product";
import ProductBlockImage from "../blocks/ProductBlockImage.vue";
import BanderoleHeader from "./BanderoleHeader.vue";

interface Props {
  title?: string;
  selectedProduct?: Product | null;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Titre de l'offre",
  selectedProduct: null,
});

const emit = defineEmits<{
  "update:title": [value: string];
  "update:selected-product": [value: Product | null];
}>();

const titleRef = ref<HTMLTextAreaElement | null>(null);
const titleHtml = ref(props.title);
const isPreviewMode = ref(false);
const hasImage = ref(!!props.selectedProduct);
const isBold = ref(false);
const isItalic = ref(false);
const selectedFont = ref("Arial");
const titleFontSize = ref(42);
const titleFontFamily = ref("Arial");
const titleFontWeight = ref("normal");
const titleFontStyle = ref("normal");

const MIN_FONT_SIZE = 20;
const MAX_FONT_SIZE = 72;

const canIncreaseFontSize = computed(() => titleFontSize.value < MAX_FONT_SIZE);
const canDecreaseFontSize = computed(() => titleFontSize.value > MIN_FONT_SIZE);

const brand = computed(() => localStorage.getItem("brand") || "agco");

const titleWidthClass = computed(() =>
  brand.value === "valtra" ? "title-width-valtra" : "title-width"
);

const titleStyle = computed(() => ({
  fontSize: `${titleFontSize.value}px`,
  fontFamily: titleFontFamily.value,
  fontWeight: titleFontWeight.value,
  fontStyle: titleFontStyle.value,
  whiteSpace: "pre-line",
  lineHeight: 1.3,
}));

const adjustFontSize = () => {
  if (!titleRef.value || isPreviewMode.value) return;

  const container = titleRef.value.parentElement;
  if (!container) return;

  const containerWidth = container.clientWidth;
  const containerHeight = container.clientHeight;
  const text = titleHtml.value;

  // Réinitialiser la taille de police pour le test
  titleRef.value.style.fontSize = `${MAX_FONT_SIZE}px`;

  // Vérifier si le texte déborde
  while (
    (titleRef.value.scrollWidth > containerWidth ||
      titleRef.value.scrollHeight > containerHeight) &&
    titleFontSize.value > MIN_FONT_SIZE
  ) {
    titleFontSize.value -= 2;
    titleRef.value.style.fontSize = `${titleFontSize.value}px`;
  }
};

// Fonction pour ajuster la taille de police en fonction du nombre de lignes
const adjustFontSizeForLines = () => {
  if (!titleRef.value || isPreviewMode.value) return;

  const lines = titleHtml.value.split("\n");
  const numLines = lines.length;

  // Ajuster la taille de base en fonction du nombre de lignes
  let baseSize = MAX_FONT_SIZE;
  if (numLines > 1) {
    baseSize = Math.max(MIN_FONT_SIZE, MAX_FONT_SIZE - (numLines - 1) * 8);
  }

  titleFontSize.value = baseSize;
  if (titleRef.value) {
    titleRef.value.style.fontSize = `${titleFontSize.value}px`;
  }
};

const handleTitleBlur = () => {
  emit("update:title", titleHtml.value);
  nextTick(() => {
    adjustFontSize();
  });
};

const toggleBold = () => {
  if (titleFontWeight.value === "bold") {
    titleFontWeight.value = "normal";
  } else {
    titleFontWeight.value = "bold";
  }
};

const toggleItalic = () => {
  if (titleFontStyle.value === "italic") {
    titleFontStyle.value = "normal";
  } else {
    titleFontStyle.value = "italic";
  }
};

const togglePreview = () => {
  isPreviewMode.value = !isPreviewMode.value;
};

const onProductDrop = (event: DragEvent) => {
  const productData = event.dataTransfer?.getData("text/plain");
  if (!productData) return;

  try {
    const data = JSON.parse(productData) as { type: string; data: Product };
    if (data.type === "product") {
      emit("update:selected-product", data.data);
    }
  } catch (error) {
    console.error("Invalid product data:", error);
  }
};

const removeProduct = () => {
  emit("update:selected-product", null);
};

const increaseFontSize = () => {
  if (titleFontSize.value < MAX_FONT_SIZE) {
    titleFontSize.value += 2;
  }
};

const decreaseFontSize = () => {
  if (titleFontSize.value > MIN_FONT_SIZE) {
    titleFontSize.value -= 2;
  }
};

watch(
  () => props.selectedProduct,
  (val) => {
    hasImage.value = !!val;
  }
);

const handleSansImage = () => {
  hasImage.value = false;
  emit("update:selected-product", null);
};

const handleAvecImage = () => {
  hasImage.value = true;
  // Si aucun produit sélectionné, on affiche la dropzone (déjà géré dans le template)
};

onMounted(() => {
  if (titleRef.value) {
    titleHtml.value = props.title;
    titleRef.value.innerHTML = titleHtml.value;
    titleRef.value.style.fontSize = `${titleFontSize.value}px`;
  }

  // Ajouter un observateur pour détecter les changements de taille du conteneur
  const resizeObserver = new ResizeObserver(() => {
    adjustFontSizeForLines();
    adjustFontSize();
  });

  if (titleRef.value?.parentElement) {
    resizeObserver.observe(titleRef.value.parentElement as unknown as Element);
  }
});

watch(titleHtml, () => {
  nextTick(() => {
    adjustFontSizeForLines();
    adjustFontSize();
  });
});

watch(
  () => props.title,
  (val) => {
    if (val !== titleHtml.value) {
      titleHtml.value = val;
    }
  }
);

const isGeneratingPdf = ref(false);

// Génère toutes les images des pages (ici une seule pour la banderole)
const generateAllPagesImages = async (): Promise<string[]> => {
  const base64Array: string[] = [];
  // Activer le mode aperçu si besoin
  const originalPreview = isPreviewMode.value;
  if (!originalPreview) isPreviewMode.value = true;
  await nextTick();

  const canvasElement = document.querySelector(".banner-canvas") as HTMLElement;
  if (!canvasElement) return base64Array;

  // Sauvegarder la taille et le transform d'origine
  const originalWidth = canvasElement.style.width;
  const originalHeight = canvasElement.style.height;
  const originalTransform = canvasElement.style.transform;
  const originalTransformOrigin = canvasElement.style.transformOrigin;

  // Taille d'affichage réelle (CSS)
  const displayWidth = 1200;
  const displayHeight = 240;
  // Taille de capture HD (10 px/mm pour 420x84 mm à 254 dpi)
  const exportWidth = 4200;
  const exportHeight = 840;
  // Facteur de zoom
  const scale = exportWidth / displayWidth;

  // Récupérer dynamiquement la couleur de fond réelle du header ou de .banner-canvas
  let bgColor = window.getComputedStyle(canvasElement).backgroundColor;
  // Si le header a un fond différent, on le prend
  const headerElement = canvasElement.querySelector(
    ".banner-container, .w-full.bg-gray-100, .w-full.bg-green-900, .w-full.bg-black"
  );
  if (headerElement) {
    const headerBg = window.getComputedStyle(headerElement).backgroundColor;
    if (
      headerBg &&
      headerBg !== "rgba(0, 0, 0, 0)" &&
      headerBg !== "transparent"
    ) {
      bgColor = headerBg;
    }

    console.log("headerElement", headerElement);
    let imgHeader = headerElement.querySelector("img");
    console.log("imgHeader", imgHeader);
    if (imgHeader) {
      imgHeader.setAttribute("style", "z-index: 100;");
    }
  }

  // Forcer la taille et le zoom pour la capture
  canvasElement.style.width = `${displayWidth}px`;
  canvasElement.style.height = `${displayHeight}px`;
  canvasElement.style.transform = `scale(${scale})`;
  canvasElement.style.transformOrigin = "top left";
  canvasElement.style.margin = "0";
  canvasElement.style.padding = "0";
  canvasElement.style.boxSizing = "border-box";
  // Ne pas forcer le background ici, on garde celui de Tailwind

  // Forcer tous les enfants à occuper 100% sans marges/paddings
  const allChildren = canvasElement.querySelectorAll("*");
  const originalStyles: {
    el: HTMLElement;
    width: string;
    height: string;
    margin: string;
    padding: string;
    boxSizing: string;
    background: string;
    fontFamily: string;
  }[] = [];
  allChildren.forEach((el) => {
    if (el instanceof HTMLElement) {
      originalStyles.push({
        el,
        width: el.style.width,
        height: el.style.height,
        margin: el.style.margin,
        padding: el.style.padding,
        boxSizing: el.style.boxSizing,
        background: el.style.background,
        fontFamily: el.style.fontFamily,
      });
      // el.style.width = "100%";

      el.style.boxSizing = "border-box";
      // Ne pas forcer le background ici, on garde celui de Tailwind
      el.style.fontFamily = window.getComputedStyle(el).fontFamily;
    }
  });

  // Appliquer la classe pdf-capture-mode
  canvasElement.classList.add("pdf-capture-mode");
  const originalBackground = canvasElement.style.backgroundColor;
  // Ne pas forcer le background ici, on garde celui de Tailwind

  // S'assurer que tous les éléments sont visibles
  const allElements = canvasElement.querySelectorAll("*");
  allElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.visibility = "visible";
      el.style.opacity = "1";
    }
  });

  // Attendre le chargement du logo (et autres images)
  const images = canvasElement.querySelectorAll("img");
  const imagePromises: Promise<void>[] = [];
  images.forEach((img) => {
    if (!img.complete) {
      imagePromises.push(
        new Promise((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
      );
    }
  });
  if (imagePromises.length > 0) {
    await Promise.all(imagePromises);
  }

  // Capture en HD avec dom-to-image, sans bgcolor pour respecter les backgrounds Tailwind réels
  const dataUrl = await domtoimage.toPng(canvasElement, {
    quality: 1,
    style: {
      boxShadow: "none",
      margin: 0,
      padding: 0,
    },
    cacheBust: true,
    useCORS: true,
    width: exportWidth,
    height: exportHeight,
    filter: () => true,
  });

  // Restaurer les styles d'origine
  canvasElement.style.width = originalWidth;
  canvasElement.style.height = originalHeight;
  canvasElement.style.transform = originalTransform;
  canvasElement.style.transformOrigin = originalTransformOrigin;
  canvasElement.style.backgroundColor = originalBackground;
  canvasElement.style.margin = "";
  canvasElement.style.padding = "";
  canvasElement.style.boxSizing = "";
  canvasElement.style.background = "";
  canvasElement.classList.remove("pdf-capture-mode");
  // Restaurer les styles des parents
  let parent = canvasElement;
  const parentOriginalStyles: {
    el: HTMLElement;
    width: string;
    maxWidth: string;
    margin: string;
  }[] = [];
  while (parent && parent !== document.body) {
    parentOriginalStyles.push({
      el: parent,
      width: parent.style.width,
      maxWidth: parent.style.maxWidth,
      margin: parent.style.margin,
    });
    parent = parent.parentElement as HTMLElement;
  }
  parentOriginalStyles.forEach(({ el, width, maxWidth, margin }) => {
    el.style.width = width;
    el.style.maxWidth = maxWidth;
    el.style.margin = margin;
  });
  // Restaurer les styles des enfants
  originalStyles.forEach(
    ({
      el,
      width,
      height,
      margin,
      padding,
      boxSizing,
      background,
      fontFamily,
    }) => {
      el.style.width = width;
      el.style.height = height;
      el.style.margin = margin;
      el.style.padding = padding;
      el.style.boxSizing = boxSizing;
      el.style.background = background;
      el.style.fontFamily = fontFamily;
    }
  );

  base64Array.push(dataUrl);

  // Restaurer le mode aperçu initial
  if (!originalPreview) isPreviewMode.value = false;
  return base64Array;
};

// Génère le PDF à partir du DOM et retourne un Blob (format web 420x84 mm, image à l'échelle 1:1)
const generatePdfBlob = async (): Promise<Blob> => {
  isGeneratingPdf.value = true;
  try {
    const base64Array: string[] = await generateAllPagesImages();
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [420, 84], // 420x84 mm fidèle à l'écran
      compress: true,
    });
    const pageWidth = 420;
    const pageHeight = 84;
    for (let i = 0; i < base64Array.length; i++) {
      const img = new window.Image();
      img.src = base64Array[i];
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = (e) => {
          console.error(
            "Erreur lors du chargement de l'image pour le PDF",
            e,
            img.src
          );
          reject(e);
        };
      });
      if (i > 0) pdf.addPage();
      pdf.addImage(
        base64Array[i],
        "PNG",
        0,
        0,
        pageWidth,
        pageHeight,
        undefined,
        "SLOW"
      );
    }
    return pdf.output("blob");
  } finally {
    isGeneratingPdf.value = false;
  }
};

const generateAndDownloadPDF = async () => {
  showScalePreview.value = true;
};

const continuePdfGeneration = async () => {
  showScalePreview.value = false;
  await generateAndDownloadPDFImpl();
};
const cancelScalePreview = () => {
  showScalePreview.value = false;
};

defineExpose({ generatePdfBlob, generateAndDownloadPDF });

const showScalePreview = ref(false);
const scalePreviewStyle = computed(() => {
  // Même scale que lors de la capture PDF
  const displayWidth = 1200;
  const displayHeight = 240;
  const exportWidth = 4200;
  const exportHeight = 840;
  const scale = exportWidth / displayWidth;
  return {
    width: displayWidth + "px",
    height: displayHeight + "px",
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    border: "1px solid #b70018",
    boxShadow: "0 0 12px #0002",
    background: "#fff",
  };
});

const isSaving = ref(false);
const banderoleCanvasRef = ref();
const toast = useToast();
const bannerCanvasDomRef = ref();

const generateAndDownloadPDFImpl = async () => {
  // Ancienne logique de génération PDF
  isSaving.value = true;
  try {
    const pdfBlob = await banderoleCanvasRef.value.generatePdfBlob();
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${titleHtml.value || "banderole"}-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error("Erreur lors de la génération du PDF :", error);
    toast.error("Erreur lors de la génération du PDF");
  } finally {
    isSaving.value = false;
  }
};

watch(showScalePreview, (val) => {
  if (val) {
    // Clone le DOM réel de .banner-canvas dans la preview
    nextTick(() => {
      const realBannerCanvas = document.querySelector(".banner-canvas");
      if (realBannerCanvas && bannerCanvasDomRef.value) {
        bannerCanvasDomRef.value.innerHTML = "";
        // On clone le DOM réel (deep clone)
        const clone = realBannerCanvas.cloneNode(true);
        bannerCanvasDomRef.value.appendChild(clone);
      }
    });
  }
});
</script>

<style scoped>
[contenteditable="true"]:focus {
  outline: 2px solid #b70018;
  border-radius: 4px;
}

[contenteditable="true"]:empty:before {
  content: attr(placeholder);
  color: #9ca3af;
  cursor: text;
}

.preview-toggle,
.style-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.style-toggle {
  padding: 0.5rem;
  min-width: 2rem;
  justify-content: center;
}

.font-select {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
  font-size: 0.9rem;
}

.preview-toggle:hover,
.style-toggle:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.block-product-image .columnOne .image-container {
  max-height: 100%;
}

.preview-toggle.active,
.style-toggle.active {
  background-color: #b70018;
  border-color: #b70018;
  color: white;
}

.preview-toggle .icon {
  font-size: 1.2rem;
}

/* Mode aperçu */
.preview-mode [contenteditable="true"] {
  outline: none !important;
  cursor: default !important;
  user-select: none !important;
}

.title-width {
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  line-height: 1.1;
  min-height: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-width-valtra {
  width: 75%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  line-height: 1.1;
  min-height: 0;
  max-width: 75%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-container {
  min-height: 120px;
  height: auto;
  width: 100%;
  max-height: 160px;
  display: flex;
  align-items: flex-start;
  padding: 1rem 2rem;
  overflow: hidden;
  margin-top: 2rem;
  margin-bottom: 1rem;
  box-sizing: border-box;
  position: relative;
  z-index: 20;
}

.valtra-title-container {
  margin-top: 0;
  position: relative;
  z-index: 20;
}

[contenteditable="true"] {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
  max-height: 100%;
  padding: 0.5rem;
  line-height: 0.7;
}

.formatting-toolbar {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 0.5rem;
  background: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 30;
}

.format-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  background-color: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.format-button:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.format-select {
  padding: 0.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  background-color: white;
  font-size: 0.9rem;
  min-width: 120px;
}

.title-container h1 {
  line-height: 1.3;
  white-space: pre-line;
  margin: 0;
  padding: 1rem;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-start;
  font-size: 42px;
}

/* Alignement spécifique pour Valtra en mode preview */
.preview-mode .title-container h1.title-width-valtra {
  justify-content: flex-start;
  text-align: left;
  margin-left: 0;
  margin-right: auto;
}

/* Styles spécifiques pour le textarea en mode Valtra */
.title-textarea.title-width-valtra {
  width: 75%;
  max-width: 75%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  line-height: 1.1;
  min-height: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  z-index: 500;
  pointer-events: auto;
}

.title-textarea {
  width: 100%;
  min-height: 60px;
  font-size: 42px;
  font-family: inherit;
  font-weight: 300;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  resize: vertical;
  line-height: 1.3;
  outline: none;
  background: #fff;
  box-sizing: border-box;
  margin: 0;
  position: relative;
  z-index: 50;
  pointer-events: auto;
  cursor: text;
}
.title-textarea:focus {
  border-color: #b70018;
  background: #f9f9f9;
}

/* Debug: rendre le textarea plus visible en mode Valtra */
.title-textarea.title-width-valtra {
  border: 2px solid #b70018 !important;
}

.banner-canvas.preview-mode {
  box-shadow: none !important;
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
  background: #fff !important;
  max-width: 100% !important;
}

/* Ajout du CSS pdf-capture-mode pour la génération PDF */
.pdf-capture-mode,
.pdf-capture-mode * {
  outline: none !important;
  border: none !important;
  box-shadow: none !important;
  caret-color: transparent !important;
}
.pdf-capture-mode [contenteditable] {
  outline: none !important;
  border: none !important;
  background: transparent !important;
  caret-color: transparent !important;
  user-select: none !important;
  pointer-events: none !important;
}
.pdf-capture-mode.banner-canvas {
  width: 1200px !important;
  height: 240px !important;
  max-width: 100% !important;
  max-height: 100% !important;
  background: #fff !important;
  box-shadow: none !important;
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
}
.pdf-capture-mode .title-container {
  min-height: 120px !important;
  max-height: 160px !important;
  display: flex !important;
  align-items: flex-start !important;
  justify-content: center !important;
  padding: 1rem 2rem !important;
  margin-top: 2rem !important;
  margin-bottom: 1rem !important;
  box-sizing: border-box !important;
  width: 100% !important;
}
.pdf-capture-mode .title-textarea,
.pdf-capture-mode h1 {
  padding: 1rem !important;
  margin: 0 !important;
  background: #fff !important;
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  align-items: flex-start !important;
  box-sizing: border-box !important;
}

/* Styles spécifiques pour Valtra en mode PDF */
.pdf-capture-mode .title-container.valtra-title-container {
  justify-content: flex-start !important;
}

.pdf-capture-mode .title-textarea.title-width-valtra,
.pdf-capture-mode h1.title-width-valtra {
  width: 75% !important;
  max-width: 75% !important;
  justify-content: flex-start !important;
  text-align: left !important;
  margin-left: 0 !important;
  margin-right: auto !important;
}

.pdf-capture-mode .banner-canvas-preview {
  background: #fff !important;
}
.pdf-capture-mode .scale-preview-canvas-wrapper {
  background: #f3f4f6 !important;
}

.banner-canvas {
  width: 2000px;
  /* height: 400px; */
  max-width: 100%;
  max-height: 100%;
}

.scale-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.scale-preview-content {
  background: #fff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 0 24px #0005;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 900px;
}
.scale-preview-header {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #b70018;
}
.scale-preview-canvas-wrapper {
  background: #f3f4f6;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.banner-canvas-preview {
  background: #fff;
  overflow: hidden;
  position: relative;
}
.scale-preview-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}
.btn-continue {
  background: #b70018;
  color: #fff;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
}
.btn-cancel {
  background: #eee;
  color: #333;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
}
</style>
